import api from './client';

/**
 * DM messaging API client — Spec 288. Consolidates block/unblock/report
 * calls that were previously scattered as raw `api.*` calls inline in
 * components (BlockedUsersSection.tsx, MessageThread.tsx).
 *
 * Route note: messagesRoutes.ts mounts at /api/messages and registers most
 * of its own routes with a leading /messages segment too (e.g.
 * '/messages/report', '/messages/:messageId') — the live path is
 * /api/messages/messages/report, not /api/messages/report.
 * See MessageThread.tsx's existing delete-message call for the same pattern.
 */

export type DmReportReason = 'harassment' | 'spam' | 'inappropriate' | 'threats' | 'other';

export interface BlockedDmUser {
  id: number;
  publicId: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  blockedAt: string;
}

export const messagesAPI = {
  // Spec 288 (report-flow relocation) — reporter selects one or more
  // messages via ConversationList's "..." menu instead of a per-message
  // trigger, so this always sends an array, even for a single selection.
  reportMessage: (messageIds: (string | number)[], reason: DmReportReason, description?: string) =>
    api.post('/messages/messages/report', { messageIds, reason, description }),

  blockUser: (userId: string | number, reason?: string) =>
    api.post(`/messages/users/${userId}/block`, reason ? { reason } : undefined),

  unblockUser: (userId: string | number) =>
    api.delete(`/messages/users/${userId}/block`),

  getBlockedUsers: () => api.get<{ data: BlockedDmUser[] }>('/messages/blocked-users'),

  // leaveConversation() route — no leading /messages segment on this one
  // route (unlike the ones above): messagesRoutes.ts registers it as plain
  // '/conversations/:conversationId', so the live path is
  // /api/messages/conversations/:conversationId. Doubles as both "delete a
  // 1:1 conversation" (Spec 288 Phase 2b) and "leave a group" (Phase 5).
  deleteConversation: (conversationId: string | number) =>
    api.delete(`/messages/conversations/${conversationId}`),

  // toggleConversationMute() route — same no-leading-/messages-segment
  // pattern as deleteConversation above: /api/messages/conversations/:id/mute.
  setConversationMuted: (conversationId: string | number, muted: boolean) =>
    api.patch(`/messages/conversations/${conversationId}/mute`, { muted }),

  // getUnreadCount() — same leading-/messages-segment pattern as
  // reportMessage() above: /api/messages/messages/unread-count.
  getUnreadCount: (signal?: AbortSignal) =>
    api.get<{ data: { unreadCount: number } }>('/messages/messages/unread-count', { signal }),
};
